/**
 * ActionBarRenderer — Data-driven action bar for AAC Keyboard & Communication Board.
 * Renders action buttons dynamically based on config order and enabled state.
 */

import React, { useMemo } from "react";
import { Delete, Volume2, Trash2, Mail, MessageSquare, Keyboard } from "lucide-react";
import { cn } from "@/lib/utils";

export interface ActionButtonDef {
  id: string;
  icon: React.ElementType;
  iconClassName?: string;
  label: string;
  onClick: () => void;
  disabled: boolean;
  /** "primary" = filled bg, "destructive" = destructive border, "default" = standard border */
  variant: "primary" | "destructive" | "default";
}

// Icon map by action id
const ACTION_ICONS: Record<string, React.ElementType> = {
  deleteWord: Delete,
  speak: Volume2,
  clear: Trash2,
  pageToggle: MessageSquare, // overridden to Keyboard in CommunicationBoard
  send: Mail,
};

const VARIANT_CLASSES = {
  primary: "bg-primary text-primary-foreground hover:bg-primary/90",
  destructive: "border-[1.5px] border-destructive/30 bg-card shadow-[0_1px_4px_hsl(195_100%_11%/0.08)] hover:bg-destructive/10",
  default: "border-[1.5px] border-border/60 bg-card shadow-[0_1px_4px_hsl(195_100%_11%/0.08)] text-foreground hover:bg-accent hover:text-accent-foreground",
};

const ICON_VARIANT_CLASSES = {
  primary: "",
  destructive: "text-destructive",
  default: "",
};

const LABEL_VARIANT_CLASSES = {
  primary: "",
  destructive: "text-foreground",
  default: "",
};

function ActionButton({ def }: { def: ActionButtonDef }) {
  const Icon = def.icon;
  return (
    <button
      onClick={def.onClick}
      disabled={def.disabled}
      data-scan-item
      className={cn(
        "w-[92px] min-h-[76px] flex flex-col items-center justify-center gap-1 py-4 px-1 rounded-xl",
        "transition-colors duration-200",
        VARIANT_CLASSES[def.variant],
        "disabled:opacity-50 disabled:pointer-events-none",
      )}
    >
      <Icon className={cn("w-6 h-6 flex-shrink-0", def.iconClassName || ICON_VARIANT_CLASSES[def.variant])} />
      <span className={cn("text-xs font-medium", LABEL_VARIANT_CLASSES[def.variant], def.id === "pageToggle" && "text-center leading-tight")}>
        {def.label}
      </span>
    </button>
  );
}

interface ActionBarSideProps {
  buttons: ActionButtonDef[];
  isRTL: boolean;
}

export function ActionBarSide({ buttons, isRTL }: ActionBarSideProps) {
  return (
    <div dir={isRTL ? "rtl" : "ltr"} className="flex gap-2 shrink-0">
      {buttons.map((def) => (
        <ActionButton
          key={def.id}
          def={def}
        />
      ))}
    </div>
  );
}

interface OrderedActionItem {
  id: string;
  enabled: boolean;
  order: number;
}

/**
 * Given the config action items, return a sorted list of enabled action IDs.
 */
export function getOrderedEnabledActions(configItems: OrderedActionItem[]): string[] {
  return [...configItems]
    .filter(i => i.enabled)
    .sort((a, b) => a.order - b.order)
    .map(i => i.id);
}

/**
 * Split ordered action IDs into two groups for left/right of text field.
 * First 2 items go to "primary side" (right in Hebrew, left in English),
 * remaining go to "secondary side".
 */
export function splitActionGroups(orderedIds: string[]): { primarySide: string[]; secondarySide: string[] } {
  return {
    primarySide: orderedIds.slice(0, 2),
    secondarySide: orderedIds.slice(2),
  };
}
